import React, { useEffect, useState } from 'react';
import {
  makeStyles,
  createStyles,
  Theme,
  Box,
  Typography,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import colors from '../../../../themes/main-colors';
import { useTranslation } from '../../../../hooks/useTranslation';
import clsx from 'clsx';
import { useOutageEventsDetails } from '../../hooks/useOutageEventsDetails';
import {
  ShowByType,
  useOutageMapOptions,
} from '../../providers/OutageMapOptionsProvider';
import { Feature } from 'geojson';
import { PspsStatus } from '../../types';
import { useOutageMapContext } from '../../providers/OutageMapProvider';
import {
  ResponsivePageView,
  useOutageResponsivePageTracker,
} from '../../providers/OutageResponsivePageTracker';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: theme.spacing(2.5),
      background: colors.cloudGrey2,
      borderBottom: `1px solid ${colors.grey2}`,
    },
    accordion: {
      boxShadow: 'none',
      backgroundColor: 'unset',
      '&:before': {
        display: 'none',
      },
    },
    summary: {
      padding: theme.spacing(0),
      minHeight: 'unset',
      '& .MuiAccordionSummary-content': {
        margin: 0,
      },
      '&.MuiAccordionSummary-root.Mui-expanded': {
        minHeight: 'unset',
      },
      cursor: 'pointer',
    },
    details: {
      padding: 0,
      backgroundColor: colors.white,
      display: 'flex',
      flexDirection: 'column',
      marginTop: theme.spacing(2.5),
    },
    row: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(2),
      padding: theme.spacing(1.25),
    },
    title: {
      fontSize: 16,
    },
    value: {
      minWidth: '4ch',
      textAlign: 'left',
      fontSize: 14,
      fontWeight: 'bold',
    },
    label: {
      fontSize: 14,
      paddingLeft: theme.spacing(1.25),
    },
    link: {
      cursor: 'pointer',
      textDecoration: 'underline',
    },
    listByRow: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1.5),
      padding: theme.spacing(1.25),
    },
  }),
);

export function OutageSummaryFooter() {
  const classes = useStyles();
  const { t } = useTranslation();
  const {
    activeOutages,
    customersAffected,
    customersWithPower,
    totalPlannedOutages,
    loading,
  } = useOutageEventsDetails();
  const { setOutageToggles, setShowBy } = useOutageMapOptions();
  const [pspsData, setPspsData] = useState<Feature[] | null>([]);
  const { resetView } = useOutageMapContext();
  const { setPage } = useOutageResponsivePageTracker();

  return (
    <Box className={classes.root}>
      <Accordion className={classes.accordion}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          className={classes.summary}
        >
          <Typography className={classes.title}>
            {customersWithPower} {t('TOTAL_PERCENT_CUSTOMERS_AFFECTED')}
          </Typography>
        </AccordionSummary>

        <AccordionDetails className={classes.details}>
          <Box className={classes.row}>
            <Typography className={classes.value}>
              {customersAffected.toLocaleString()}
            </Typography>
            <Typography className={classes.label}>
              {t('CUSTOMERS_AFFECTED')}
            </Typography>
          </Box>
          <Divider />

          <Box className={classes.row}>
            <Typography className={classes.value}>
              {activeOutages.toLocaleString()}
            </Typography>
            <Link
              className={clsx(classes.label, classes.link)}
              onClick={() => {
                setShowBy(ShowByType.DEFAULT);
                setOutageToggles({
                  planned: true,
                  unplanned: true,
                  psps: false,
                });
                setPage(ResponsivePageView.Map);
                resetView();
              }}
            >
              {t('ACTIVE_OUTAGES')}
            </Link>
          </Box>
          <Divider />

          <Box className={classes.row}>
            <Typography className={classes.value}>
              {totalPlannedOutages}
            </Typography>
            <Link
              className={clsx(classes.label, classes.link)}
              onClick={() => {
                setShowBy(ShowByType.DEFAULT);
                setOutageToggles({
                  planned: true,
                  unplanned: false,
                  psps: false,
                });
                setPage(ResponsivePageView.Map);
                resetView();
              }}
            >
              {t('PLANNED_OUTAGES')}
            </Link>
          </Box>
          <Divider />

          <Box className={classes.listByRow}>
            <Typography className={classes.label}>{t('LIST_BY')}</Typography>
            <a
              className={clsx(classes.label, classes.link)}
              onClick={() => {
                setShowBy(ShowByType.COUNTY);
                setPage(ResponsivePageView.Map);
                resetView();
              }}
            >
              {t('COUNTY')}
            </a>
            <a
              className={clsx(classes.label, classes.link)}
              onClick={() => {
                setShowBy(ShowByType.ZIP);
                setPage(ResponsivePageView.Map);
                resetView();
              }}
            >
              {t('ZIP_CODE')}
            </a>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
